/**
 * AuthLayout — Full-screen auth wrapper with AWENI branding.
 * Glass-morphism card, gradient background, language selector.
 * Mobile-first, RTL-aware, WCAG AA compliant.
 */

import type { AweniContext } from '../../../context/AweniContext';

const LANG_FLAGS: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  ar: 'AR',
  es: 'ES',
  de: 'DE',
};

export function createAuthLayout(ctx: AweniContext): {
  wrapper: HTMLElement;
  card: HTMLElement;
  setTitle: (title: string) => void;
  setSubtitle: (subtitle: string) => void;
  showError: (msg: string) => void;
  clearError: () => void;
  showSuccess: (msg: string) => void;
  clearMessages: () => void;
} {
  // Inject styles once
  _injectAuthStyles();

  const wrapper = document.createElement('div');
  wrapper.className = 'auth-layout';
  wrapper.setAttribute('dir', ctx.i18n.dir);

  // Content container (scrollable)
  const content = document.createElement('div');
  content.className = 'auth-content';

  // ── LANGUAGE BAR (STEP 0 — top, before anything else) ──────────────────────
  // Non-English users must be able to switch language before reading any copy.
  const langBar = document.createElement('div');
  langBar.className = 'auth-lang-bar';
  for (const locale of ctx.i18n.supportedLocales) {
    const btn = document.createElement('button');
    btn.className = `auth-lang-btn${locale === ctx.i18n.locale ? ' active' : ''}`;
    btn.textContent = LANG_FLAGS[locale] || locale.toUpperCase();
    btn.setAttribute('aria-label', `Switch to ${locale}`);
    btn.addEventListener('click', () => {
      ctx.i18n.setLocale(locale);
      wrapper.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr');
      langBar.querySelectorAll('.auth-lang-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
    langBar.appendChild(btn);
  }
  content.appendChild(langBar);

  // ── BRAND ──────────────────────────────────────────────────────────────────
  const brand = document.createElement('div');
  brand.className = 'auth-brand';
  brand.innerHTML = `
    <div class="auth-logo">
      <img src="/aweni/icons/pwa-192x192.png" alt="AWENI" width="72" height="72" style="display:block;border-radius:20px;" />
    </div>
    <div class="auth-brand-name">AWENI</div>
  `;
  content.appendChild(brand);

  // ── TITLE + SUBTITLE ───────────────────────────────────────────────────────
  const titleEl = document.createElement('h1');
  titleEl.className = 'auth-title';
  content.appendChild(titleEl);

  const subtitleEl = document.createElement('p');
  subtitleEl.className = 'auth-subtitle';
  content.appendChild(subtitleEl);

  // ── MESSAGE AREA (errors/success) ──────────────────────────────────────────
  const messageEl = document.createElement('div');
  messageEl.className = 'auth-message';
  messageEl.setAttribute('role', 'alert');
  messageEl.setAttribute('aria-live', 'polite');
  content.appendChild(messageEl);

  // ── CARD (auth choices / form) ─────────────────────────────────────────────
  const card = document.createElement('div');
  card.className = 'auth-card';
  content.appendChild(card);

  wrapper.appendChild(content);

  return {
    wrapper,
    card,
    setTitle(title: string) {
      titleEl.textContent = title;
    },
    setSubtitle(subtitle: string) {
      subtitleEl.textContent = subtitle;
      subtitleEl.style.display = subtitle ? '' : 'none';
    },
    showError(msg: string) {
      messageEl.innerHTML = `<div class="auth-msg auth-msg-error">${_escapeHtml(msg)}</div>`;
    },
    clearError() {
      const errEl = messageEl.querySelector('.auth-msg-error');
      if (errEl) errEl.remove();
    },
    showSuccess(msg: string) {
      messageEl.innerHTML = `<div class="auth-msg auth-msg-success">${_escapeHtml(msg)}</div>`;
    },
    clearMessages() {
      messageEl.innerHTML = '';
    },
  };
}

function _escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function _injectAuthStyles(): void {
  if (document.getElementById('aweni-auth-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-auth-styles';
  style.textContent = `
    /* ======================================================
       Auth layout — AWENI Design System v1
       Cream background, clean light card, lang pills at top.
       Single entry point for the entire app.
       ====================================================== */
    .auth-layout {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--bg-primary, #FAF7F2);
      overflow-y: auto;
    }

    .auth-content {
      width: 100%;
      max-width: 400px;
      padding: 0 20px;
      padding-top: max(16px, env(safe-area-inset-top, 16px));
      padding-bottom: max(24px, env(safe-area-inset-bottom, 24px));
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
    }

    /* ── Language bar — FIRST element, step 0 ── */
    .auth-lang-bar {
      display: flex;
      gap: 6px;
      justify-content: center;
      margin-bottom: 32px;
      animation: authFadeIn var(--motion-duration-slow) var(--motion-easing-enter);
    }

    .auth-lang-btn {
      padding: 5px 13px;
      border-radius: 20px;
      border: 1px solid var(--border, rgba(10,10,10,0.10));
      background: transparent;
      color: var(--text-secondary, #888);
      font-size: 0.72rem;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--motion-duration-fast) var(--motion-easing-default);
      min-height: 32px;
      min-width: 40px;
    }

    .auth-lang-btn:hover,
    .auth-lang-btn:focus-visible {
      background: rgba(10,10,10,0.05);
      color: var(--text-primary, #0A0A0A);
    }

    .auth-lang-btn.active {
      background: var(--text-primary, #0A0A0A);
      color: var(--bg-primary, #FAF7F2);
      border-color: var(--text-primary, #0A0A0A);
    }

    /* ── Brand ── */
    .auth-brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      animation: authFadeIn var(--motion-duration-slow) var(--motion-easing-enter);
    }

    .auth-logo {
      filter: drop-shadow(0 6px 16px rgba(10,10,10,0.12));
    }

    .auth-brand-name {
      font-size: 20px;
      font-weight: 800;
      letter-spacing: 3px;
      color: var(--text-primary, #0A0A0A);
    }

    /* ── Title + subtitle ── */
    .auth-title {
      font-size: 1.4rem;
      font-weight: 700;
      color: var(--text-primary, #0A0A0A);
      text-align: center;
      margin: 0 0 6px;
      animation: authFadeIn var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-fast) both;
    }

    .auth-subtitle {
      font-size: 0.85rem;
      color: var(--accent, #C8A96A);
      text-align: center;
      margin: 0 0 24px;
      font-weight: 500;
      animation: authFadeIn var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-fast) both;
    }

    /* ── Message (errors/success) ── */
    .auth-message {
      width: 100%;
      min-height: 0;
    }

    .auth-msg {
      padding: 10px 14px;
      border-radius: 10px;
      font-size: 0.85rem;
      margin-bottom: 12px;
      animation: authSlideDown var(--motion-duration-base) var(--motion-easing-enter);
    }

    .auth-msg-error {
      background: rgba(239, 68, 68, 0.08);
      color: #DC2626;
      border: 1px solid rgba(239, 68, 68, 0.18);
    }

    .auth-msg-success {
      background: rgba(16, 185, 129, 0.08);
      color: #065F46;
      border: 1px solid rgba(16, 185, 129, 0.18);
    }

    /* ── Card (auth choices / forms) ── */
    .auth-card {
      width: 100%;
      background: #FFFFFF;
      border: 1px solid var(--border, rgba(10,10,10,0.08));
      border-radius: 20px;
      padding: 20px;
      box-shadow: 0 2px 16px rgba(10,10,10,0.06), 0 8px 32px rgba(10,10,10,0.04);
      animation: authFadeIn var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-base) both;
    }

    /* ---- Form elements ---- */

    .auth-field {
      margin-bottom: 16px;
    }

    .auth-label {
      display: block;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--text-secondary, #94A3B8);
      margin-bottom: 6px;
    }

    .auth-input-wrap {
      position: relative;
    }

    .auth-input {
      width: 100%;
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid var(--border, rgba(10,10,10,0.12));
      background: var(--bg-primary, #FAF7F2);
      color: var(--text-primary, #0A0A0A);
      font-size: 0.95rem;
      outline: none;
      transition: border-color var(--motion-duration-fast) var(--motion-easing-default), box-shadow var(--motion-duration-fast) var(--motion-easing-default);
      box-sizing: border-box;
    }

    .auth-input::placeholder {
      color: var(--text-secondary, #999);
    }

    .auth-input:focus {
      border-color: rgba(10, 10, 10, 0.5);
      box-shadow: 0 0 0 3px rgba(10, 10, 10, 0.1);
    }

    .auth-input.error {
      border-color: rgba(239, 68, 68, 0.5);
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }

    .auth-field-error {
      font-size: 0.75rem;
      color: #DC2626;
      margin-top: 4px;
    }

    .auth-toggle-pw {
      position: absolute;
      inset-inline-end: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: var(--text-secondary, #94A3B8);
      cursor: pointer;
      padding: 4px;
      min-width: 44px;
      min-height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .auth-btn {
      width: 100%;
      padding: 14px;
      border-radius: 12px;
      border: none;
      font-size: 1rem;
      font-weight: 700;
      cursor: pointer;
      transition: all var(--motion-duration-fast) var(--motion-easing-default);
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .auth-btn-primary {
      background: var(--accent);
      color: var(--accent-text);
    }

    .auth-btn-primary:hover:not(:disabled) {
      filter: brightness(1.1);
      transform: translateY(-1px);
    }

    .auth-btn-primary:active:not(:disabled) {
      transform: translateY(0);
    }

    .auth-btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .auth-btn-secondary {
      background: transparent;
      color: var(--text-secondary, #555);
      border: 1px solid var(--border, rgba(10,10,10,0.10));
    }

    .auth-link {
      color: var(--accent);
      text-decoration: none;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
    }

    .auth-link:hover {
      text-decoration: underline;
    }

    .auth-divider {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 16px 0;
      color: var(--text-secondary, #64748B);
      font-size: 0.8rem;
    }

    .auth-divider::before,
    .auth-divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--border, rgba(10,10,10,0.08));
    }

    .auth-footer-row {
      text-align: center;
      margin-top: 16px;
      font-size: 0.85rem;
      color: var(--text-secondary, #94A3B8);
    }

    /* ---- Password strength ---- */

    .auth-pw-strength {
      display: flex;
      gap: 4px;
      margin-top: 8px;
    }

    .auth-pw-bar {
      flex: 1;
      height: 4px;
      border-radius: 2px;
      background: var(--border, rgba(10,10,10,0.08));
      transition: background var(--motion-duration-base) var(--motion-easing-default);
    }

    .auth-pw-label {
      font-size: 0.72rem;
      margin-top: 4px;
      text-align: end;
    }

    /* ---- Spinner ---- */

    .auth-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid transparent;
      border-top-color: currentColor;
      border-radius: 50%;
      animation: authSpin var(--motion-duration-deliberate) linear infinite;
    }

    /* ---- Animations ---- */

    @keyframes authFadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes authSlideDown {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes authSpin {
      to { transform: rotate(360deg); }
    }

    /* ---- Responsive ---- */

    @media (max-width: 480px) {
      .auth-content {
        padding: 16px 16px;
        padding-top: max(16px, env(safe-area-inset-top, 16px));
      }
      .auth-card {
        padding: 20px 16px;
        border-radius: 16px;
      }
      .auth-title {
        font-size: 1.25rem;
      }
    }

    /* ---- Light theme overrides ---- */

    [data-theme="light"] .auth-layout,
    :root:not(.dark) .auth-layout {
      background: #F8FAFC;
    }

    [data-theme="light"] .auth-bg-decor,
    :root:not(.dark) .auth-bg-decor {
      background:
        radial-gradient(ellipse 60% 50% at 20% 20%, rgba(10, 10, 10, 0.06), transparent),
        radial-gradient(ellipse 50% 40% at 80% 80%, rgba(59, 130, 246, 0.05), transparent);
    }

    [data-theme="light"] .auth-card,
    :root:not(.dark) .auth-card {
      background: rgba(255, 255, 255, 0.85);
      border-color: rgba(0, 0, 0, 0.08);
    }

    [data-theme="light"] .auth-input,
    :root:not(.dark) .auth-input {
      background: rgba(255, 255, 255, 0.8);
      border-color: rgba(0, 0, 0, 0.1);
      color: #1E293B;
    }

    /* ---- Turnstile ---- */

    .auth-turnstile {
      display: flex;
      justify-content: center;
      margin: 12px 0;
      min-height: 65px;
    }

    /* ---- Check your email screen ---- */

    .auth-check-email {
      text-align: center;
      padding: 16px 0;
    }

    .auth-check-email-icon {
      margin: 0 auto 16px;
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: rgba(10, 10, 10, 0.12);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .auth-check-email h2 {
      font-size: 1.2rem;
      font-weight: 700;
      color: var(--text-primary, #F1F5F9);
      margin: 0 0 8px;
    }

    .auth-check-email p {
      font-size: 0.9rem;
      color: var(--text-secondary, #94A3B8);
      margin: 0 0 20px;
      line-height: 1.5;
    }
  `;
  document.head.appendChild(style);
}
